/**
 * TimePicker Component
 * 
 * Specialized input component for lap times in MM:SS.MSMSMS format
 * Handles input validation and formatting without crashes
 */

import React, { useState, useRef, useEffect } from "react";

interface TimePickerProps {
  label: string;
  value: number; // Value in seconds
  onChange: (seconds: number) => void;
  placeholder?: string;
  disabled?: boolean;
  unit?: string;
}

export const TimePicker: React.FC<TimePickerProps> = ({
  label,
  value,
  onChange,
  placeholder = "01:47.500",
  disabled = false,
  unit
}) => {
  const [inputValue, setInputValue] = useState("");
  const lastValueRef = useRef(value);
  const isFocusedRef = useRef(false);

  // Convert seconds to MM:SS.MSMSMS format
  const formatTime = (seconds: number): string => {
    if (!seconds || seconds <= 0) return "";
    // Use integer millisecond math to avoid floating-point imprecision (e.g. 0.6 * 1000 = 599.999...)
    const totalMs = Math.round(seconds * 1000);
    const minutes = Math.floor(totalMs / 60000);
    const secs = Math.floor((totalMs % 60000) / 1000);
    const ms = totalMs % 1000;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  };

  // Parse MM:SS.MSMSMS format to seconds
  const parseTime = (timeString: string): number => {
    if (!timeString || timeString.trim() === '') {
      return 0;
    }
    // Allow partial input while typing
    const cleanInput = timeString.replace(/[^0-9:.]/g, '');
    if (cleanInput.includes(':')) {
      const parts = cleanInput.split(':');
      // MM:SS:MSMSMS (two colons)
      if (parts.length === 3) {
        const minutes = parseInt(parts[0]) || 0;
        const seconds = parseInt(parts[1]) || 0;
        let msString = parts[2].padEnd(3, '0').substring(0, 3);
        const milliseconds = parseInt(msString) || 0;
        if (seconds >= 60) return 0;
        return minutes * 60 + seconds + milliseconds / 1000;
      }
      // MM:SS.MSMSMS (one colon, decimal)
      else if (parts.length === 2) {
        const minutes = parseInt(parts[0]) || 0;
        const secondsPart = parts[1];
        if (secondsPart.includes('.')) {
          const [seconds, msRaw] = secondsPart.split('.');
          const secs = parseInt(seconds) || 0;
          let msString = (msRaw || '').padEnd(3, '0').substring(0, 3);
          const ms = parseInt(msString) || 0;
          if (secs >= 60) return 0;
          return minutes * 60 + secs + ms / 1000;
        } else if (secondsPart.length > 0) {
          const secs = parseInt(secondsPart) || 0;
          if (secs >= 60) return 0;
          return minutes * 60 + secs;
        } else {
          return minutes * 60;
        }
      } else if (parts.length === 1 && cleanInput.endsWith(':')) {
        const minutes = parseInt(parts[0]) || 0;
        return minutes * 60;
      }
    } else {
      // Just numbers - treat as seconds
      const numericValue = parseFloat(cleanInput);
      if (!isNaN(numericValue)) {
        return numericValue;
      }
    }
    return 0;
  };

  // Initialize input value when component mounts or value changes externally
  useEffect(() => {
    if (!isFocusedRef.current && lastValueRef.current !== value) {
      setInputValue(formatTime(value));
      lastValueRef.current = value;
    }
  }, [value]);

  // Initialize on mount
  useEffect(() => {
    if (value > 0) {
      setInputValue(formatTime(value));
      lastValueRef.current = value;
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValue = e.target.value;
    setInputValue(newInputValue);

    if (newInputValue.trim() === '') {
      onChange(0);
      lastValueRef.current = 0;
      return;
    }

    const parsedSeconds = parseTime(newInputValue);
    if (parsedSeconds > 0) {
      onChange(parsedSeconds);
      lastValueRef.current = parsedSeconds;
    }
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    const parsedSeconds = parseTime(inputValue);
    
    if (parsedSeconds > 0) {
      const formattedValue = formatTime(parsedSeconds);
      setInputValue(formattedValue);
      onChange(parsedSeconds);
      lastValueRef.current = parsedSeconds;
    } else if (inputValue.trim() !== '') {
      // If user typed something but it's invalid, keep the input as-is
      // Don't clear it, just don't update the parent
      setInputValue(inputValue);
    } else {
      // Only clear if the input is completely empty
      setInputValue("");
      onChange(0);
      lastValueRef.current = 0;
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-300">
        {label}
        {unit && <span className="text-slate-400 ml-1">({unit})</span>}
      </label>
      
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 bg-slate-800 rounded-xl border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
};
